import React, { useEffect, useMemo, useState, Fragment } from 'react'
import { Translate } from 'react-localize-redux'
import {
  Select,
  MenuItem,
  Box,
  FormControl,
  Grid,
  Typography,
  makeStyles,
  IconButton,
  ListSubheader,
} from '@material-ui/core'
import { connect } from 'react-redux'
import { matches, lineups as lineupsModule } from 'redux/modules'
import AlertDialog from 'components/AlertDialog'
import DeleteIcon from '@material-ui/icons/Delete'
import PropTypes from 'prop-types'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import useTeamFeature from 'hooks/useTeamFeature'
import LineupEditModeToggler from './LineupEditModeToggler'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles((theme) => ({
  linupSelect: {
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: ' translate(14px, -6px) scale(0.75)',
      background: theme.palette.common.white,
      padding: theme.spacing(0, 1),
    },
    marginBottom: 4
  },
  deleteIconButton: {
    padding: 0,
  },
  dropdownItemsWrapper: {
    maxHeight: '500px',
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: `1px 1px 12px 0 ${theme.palette.grey[400]}`,
    '& .MuiListSubheader-root': {
      fontWeight: 'bold',
    },
    '& .MuiListItem-root': {
      paddingLeft: theme.spacing(4),
    },
  },
}))

const LineupsFilter = ({
  lineups,
  currentMatch,
  lineupPositions,
  updateMatch,
  updateMatchParticipantsInBulk,
  participants,
  matchLineup,
  deleteLineup,
  isEditMode,
  handleEdit,
  handleSave,
  handleCancel,
}) => {
  const classes = useStyles()
  const [activeLineupUuid, setActiveLineupUuid] = useState(null)
  const [changeLineupAlertOpen, setChangeLineupAlertOpen] = useState(false)
  const [selectedLineupUuid, setSelectedLineupUuid] = useState()
  const [lineupDeleteAlertOpen, setLineupDeleteAlertOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasPlayerAvailabilityEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_availability_enabled
  )

  const groupedLineups = useMemo(() => {
    // Logic to group all the lineups by their groups
    if (lineups.length) {
      return lineups.reduce((groupedData, lineup) => {
        const { group } = lineup

        // Check if the group already exists in the group data
        const groupIndex = groupedData.findIndex((item) => item.name === group)

        if (groupIndex === -1) {
          // If the group doesn't exist, add it to the group data
          groupedData.push({ name: group, lineups: [lineup] })
        } else {
          // If the group already exists, push the lineup to its lineups array
          groupedData[groupIndex].lineups.push(lineup)
        }

        return groupedData
      }, [])
    }

    // Return an empty array if lineups.length === 0
    return []
  }, [lineups]) // Recalculates whenever lineups changes

  const handleLineupChangeSubmit = (uuid) => {
    if (!uuid) {
      return
    }

    // Reset all participants x=null,y=null and participant=false
    let matchParticipants = participants.map((p) => {
      return { ...p, position_x: null, position_y: null, participant: false }
    })

    // Now get the selected lineup
    const selectedLineup = lineups.find((l) => l.uuid === uuid)
    setActiveLineupUuid(selectedLineup.uuid)

    // Bind the participants according to the lineup positions if had player_uuid
    selectedLineup.lineup.forEach((p) => {
      matchParticipants = matchParticipants.map((mp) => {
        if (mp.player_uuid === p.player_uuid) {
          return {
            ...mp,
            position_x: p.x,
            position_y: p.y,
            participant: true,
          }
        }

        return mp
      })
    })

    updateMatch(currentMatch.uuid, {
      lineup_uuid: selectedLineup.uuid,
      preventToastNotification: true,
    })

    // Close lineups menu
    setMenuOpen(false)

    // Only update participants in bulk if they are on the field otherwise do nothing
    if (matchParticipants.length > 0) {
      updateMatchParticipantsInBulk(currentMatch.uuid, {
        participants: matchParticipants.map((p) => {
          return {
            uuid: p.uuid,
            position_x: p.position_x,
            position_y: p.position_y,
          }
        }),
      })
    }
  }

  const handleLineupChange = (uuid) => {
    // Need to be updated as per team participants
    const selectedParticipants = participants.filter((p) => p.participant)
    if (selectedParticipants.length > 0) {
      setChangeLineupAlertOpen(true)
      setSelectedLineupUuid(uuid)
    } else {
      handleLineupChangeSubmit(uuid)
    }
  }

  const handleAlertClose = () => {
    setChangeLineupAlertOpen(false)
    setSelectedLineupUuid(null)
  }

  const handleAlertConfirm = () => {
    handleLineupChangeSubmit(selectedLineupUuid)
    handleAlertClose()
    setSelectedLineupUuid(null)
  }

  useEffect(() => {
    setActiveLineupUuid(matchLineup ? matchLineup.uuid : null)
  }, [matchLineup])

  const handleDeleteClick = (uuid, event) => {
    // Prevent the event from bubbling up to the Select component
    // and triggering the onChange event
    event.stopPropagation()

    if (featureGuard(hasPlayerAvailabilityEnabled)) return

    setLineupDeleteAlertOpen(true)
    setSelectedLineupUuid(uuid)
  }
  const handleDeleteAlertConfirm = () => {
    setLineupDeleteAlertOpen(false)
    deleteLineup(selectedLineupUuid)
    setSelectedLineupUuid(null)
  }
  const handleDeleteAlertClose = () => {
    setLineupDeleteAlertOpen(false)
    setSelectedLineupUuid(null)
  }

  const getDeletedLineupTranslatedLabel = (title) => {
    return (
      <>
        (<Translate id="matches.deleted" />) {title}
      </>
    )
  }

  return (
    <>
      {lineupDeleteAlertOpen && (
        <AlertDialog
          open={lineupDeleteAlertOpen}
          onClose={handleDeleteAlertClose}
          onSuccess={handleDeleteAlertConfirm}
          onCancel={handleDeleteAlertClose}
          title={<Translate id="button.delete" />}
          subTitle={<Translate id="events.are-you-sure-you-want-to-delete" />}
          submitButtonLabel={<Translate id="button.ok" />}
          cancelButtonLabel={<Translate id="button.close" />}
        />
      )}
      <Box mx={1} mb={0.5}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={4}>
            <FormControl
              variant="outlined"
              className={classes.linupSelect}
            >
              <Select
                id="match-lineup-filter"
                value={activeLineupUuid || ''}
                renderValue={(selected) => {
                  const selectedLineup = lineups.find(
                    (l) => l.uuid === selected
                  )

                  if (selectedLineup) return selectedLineup.title

                  if (matchLineup)
                    return getDeletedLineupTranslatedLabel(matchLineup.title)

                  return ''
                }}
                MenuProps={{
                  classes: { paper: classes.dropdownItemsWrapper }, // Apply custom styles to the menu,
                  elevation: 0,
                }}
                // On Change method not working after list items grouping so will manage dorpdown open state manually
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                onOpen={() => setMenuOpen(true)}
              >
                {groupedLineups.flatMap((group) => [
                  <ListSubheader
                    disableSticky
                    color="primary"
                    key={`subheader-${group.name}`}
                  >
                    {group.name === '%TEAM%' ? (
                      <Translate id="matches.saved-lineups" />
                    ) : (
                      group.name
                    )}
                  </ListSubheader>,
                  ...group.lineups.map((lineup) => (
                    <MenuItem
                      value={lineup.uuid}
                      key={lineup.uuid}
                      onClick={() => handleLineupChange(lineup.uuid)}
                    >
                      <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        {lineup.title}
                        {lineup.can.includes('delete') && (
                          <IconButton
                            className={classes.deleteIconButton}
                            onClick={(event) =>
                              handleDeleteClick(lineup.uuid, event)
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Grid>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            {!lineupPositions?.length && (
              <Typography variant="body2">
                <Translate id="matches.no-available-players-message" />
              </Typography>
            )}
          </Grid>
          <Grid item xs={4} component={Box} textAlign="right">
            <LineupEditModeToggler
              isEditMode={isEditMode}
              handleEdit={handleEdit}
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          </Grid>
        </Grid>
      </Box>
      {changeLineupAlertOpen && (
        <AlertDialog
          open={changeLineupAlertOpen}
          onClose={handleAlertClose}
          onSuccess={handleAlertConfirm}
          onCancel={handleAlertClose}
          title={<Translate id="matches.change-lineup" />}
          subTitle={<Translate id="matches.confirm-lineup-change-message" />}
          submitButtonLabel={<Translate id="button.ok" />}
          cancelButtonLabel={<Translate id="button.close" />}
        />
      )}
    </>
  )
}

LineupsFilter.defaultProps = {
  lineups: [],
}

LineupsFilter.propTypes = {
  lineups: PropTypes.arrayOf(PropTypes.shape().isRequired),
  lineupPositions: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  currentMatch: PropTypes.shape().isRequired,
  updateMatch: PropTypes.func.isRequired,
  updateMatchParticipantsInBulk: PropTypes.func.isRequired,
  participants: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  matchLineup: PropTypes.shape({
    title: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  deleteLineup: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
}

export default connect(
  ({ matches: { current } }) => ({
    currentMatch: current,
    participants: current.relationships.participants,
    matchLineup: current.relationships.lineup,
  }),
  {
    updateMatch: matches.updateMatch,
    updateMatchParticipantsInBulk: matches.updateMatchParticipantsInBulk,
    deleteLineup: lineupsModule.deleteLineup,
  }
)(LineupsFilter)
